import React from 'react';
import { Table, Tag, Button, Space, Tooltip, Popconfirm } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CrmTaskDTO, TaskPriority, TaskStatus } from '../types';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
    HIGH: 'error',
    MEDIUM: 'warning',
    LOW: 'success',
};

const STATUS_COLOR: Record<TaskStatus, string> = {
    OPEN: 'blue',
    IN_PROGRESS: 'processing',
    DONE: 'success',
    CANCELLED: 'default',
};

interface Props {
    tasks: CrmTaskDTO[];
    loading?: boolean;
    onEdit?: (task: CrmTaskDTO) => void;
    onComplete?: (id: number) => void;
    onCancel?: (id: number) => void;
    showCustomer?: boolean;
}

const TaskTable: React.FC<Props> = ({ tasks, loading, onEdit, onComplete, onCancel, showCustomer = true }) => {
    const columns: ColumnsType<CrmTaskDTO> = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 90,
            render: (p: TaskPriority) => <Tag color={PRIORITY_COLOR[p]}>{p}</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (s: TaskStatus) => (
                <Tag color={STATUS_COLOR[s]}>{s.replace('_', ' ')}</Tag>
            ),
        },
        {
            title: 'Due Date',
            dataIndex: 'dueDate',
            key: 'dueDate',
            width: 110,
            sorter: (a, b) => a.dueDate.localeCompare(b.dueDate),
            render: (date: string) => {
                const isOverdue = date < new Date().toISOString().substring(0, 10);
                return <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>{date}</span>;
            },
        },
        {
            title: 'Assigned To',
            dataIndex: 'assignedTo',
            key: 'assignedTo',
            width: 120,
        },
        ...(showCustomer ? [{
            title: 'Customer',
            dataIndex: 'custNo',
            key: 'custNo',
            width: 100,
            render: (c: string) => c || '—',
        }] : []),
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_: any, record: CrmTaskDTO) => (
                <Space>
                    {onEdit && (
                        <Tooltip title="Edit">
                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
                        </Tooltip>
                    )}
                    {onComplete && record.status !== 'DONE' && record.status !== 'CANCELLED' && (
                        <Tooltip title="Mark Done">
                            <Popconfirm title="Mark as done?" onConfirm={() => onComplete(record.id!)}>
                                <Button type="text" size="small" icon={<CheckOutlined />} />
                            </Popconfirm>
                        </Tooltip>
                    )}
                    {onCancel && record.status !== 'DONE' && record.status !== 'CANCELLED' && (
                        <Tooltip title="Cancel Task">
                            <Popconfirm title="Cancel this task?" onConfirm={() => onCancel(record.id!)}>
                                <Button type="text" size="small" danger icon={<CloseOutlined />} />
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Table
            rowKey="id"
            columns={columns}
            dataSource={tasks}
            loading={loading}
            size="small"
            pagination={false}
        />
    );
};

export default TaskTable;
